import { useQuery, useMutation } from '@apollo/client'
import { ToastContext } from 'contexts'
import { useContext } from 'react'
import createAccountGql from 'gql/account/CreateAccount.gql'
import getAccountsGql from 'gql/account/getAccounts.gql'
import updateAccountGql from 'gql/account/updateAccount.gql'
import switchAccountGql from 'gql/account/switchAccount.gql'
import updateAccountTypeGql from 'gql/account/updateAccountType.gql'
import { accountTypeEnum } from 'types/account'
interface UseSwitchAccountServiceReturn {
  switchAccount: any
  loading: boolean
}

export const useCreateAccountService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(createAccountGql)

  const createAccount = async (input: any) => {
    try {
      const {
        data: { CreateAccount },
      } = await mutation({
        variables: {
          input: input,
        },
      })

      return CreateAccount
    } catch (error) {
      setToast({
        message: (error as Error)?.message ?? 'Error creating Account',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { createAccount, loading }
}

export const useGetAccounts = () => {
  const { data, error, loading, refetch } = useQuery(getAccountsGql)

  try {
    if (error) {
      // Handle error here
      console.error('Error fetching accounts:', error)
    }
  } catch (error) {
    // Handle any unexpected errors
    console.error('An unexpected error occurred:', error)
  }

  return {
    data: data?.getAccounts || [],
    error,
    loading,
    refetch,
  }
}

export const useUpdateAccountService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(updateAccountGql, {
    update(
      cache,
      {
        data: {
          UpdateAccount: { account },
        },
      },
    ) {
      cache.modify({
        fields: {
          account(existingAccount = {}, { readField }) {
            return account.id === readField('id', existingAccount.ref)
              ? account
              : existingAccount.ref
          },
        },
      })
    },
  })

  const updateAccount = async (input: any) => {
    try {
      const {
        data: { UpdateAccount },
      } = await mutation({
        variables: {
          input: input,
        },
      })

      return UpdateAccount
    } catch (error) {
      setToast({
        message: (error as Error)?.message ?? 'Error updating Account',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { updateAccount, loading }
}

/**
 * Function to switch the current account.
 *
 * @param {string} account_id - The ID of the account to switch to.
 * @return {Promise<UseSwitchAccountServiceReturn>} The switched account data.
 */
export const useSwitchAccountService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(switchAccountGql)

  const switchAccount = async (account_id: string): Promise<UseSwitchAccountServiceReturn> => {
    try {
      const {
        data: { switchAccount },
      } = await mutation({
        variables: {
          account_id,
        },
      })

      return switchAccount
    } catch (error: any) {
      setToast({
        message: error.message ?? 'Error Switching Account',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { switchAccount, loading }
}

export const useUpdateAccountTypeService = () => {
  const { setToast } = useContext(ToastContext)
  const [mutation, { loading }] = useMutation(updateAccountTypeGql)

  const updateAccountType = async (input: { account_type: accountTypeEnum }) => {
    try {
      const {
        data: { updateAccountType },
      } = await mutation({
        variables: {
          input,
        },
      })

      return updateAccountType
    } catch (error: any) {
      setToast({
        message: error.message ?? 'Error Updating Account Type',
        type: 'negative',
        open: true,
      })
      throw error // Rethrow the error to propagate it to the caller
    }
  }

  return { updateAccountType, loading }
}
